import React, { useState, useRef, useEffect } from "react";
import AuthLayout from "./AuthLayout";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { verifyOtp } from "../../store/authSlice";
import { useNavigate, useLocation } from "react-router-dom";
import OtpVerifyIcon from "../../assets/auth/otp-verify.svg";
import { Loader } from "lucide-react";

interface LocationState {
  phoneNumber?: string;
  email?: string;
  type?: string;
  registrationType?: string;
  invitation?: any;
  organization?: any;
}

const OtpVerification = () => {
  const location = useLocation();
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { fetchLoading } = useAppSelector((state) => state.auth);
  const user = useAppSelector((state) => state.auth.user);

  const phoneNumber = (location.state as LocationState)?.phoneNumber;
  const email = (location.state as LocationState)?.email;
  const type = (location.state as LocationState)?.type;
  const registrationType = (location.state as LocationState)?.registrationType;
  const invitation = (location.state as LocationState)?.invitation;
  const organization = (location.state as LocationState)?.organization;

  const [resendTimer, setResendTimer] = useState<number>(30); // 30 seconds timer
  const [canResend, setCanResend] = useState<boolean>(false);
  const [loading, setLoading] = useState(false); // local loading state

  // Add timer effect
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  // Format timer to display minutes and seconds
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleChange = (index: number, value: string) => {
    if (/^\d*$/.test(value)) {
      // Always set the new value without checking existing value
      const newOtp = [...otp];
      newOtp[index] = value.slice(-1); // Take only the last digit if multiple digits are pasted
      setOtp(newOtp);

      // Auto-focus next input if value is entered
      if (value && index < 3) {
        inputRefs.current[index + 1]?.focus();
      }

      // Clear error when user starts typing
      if (error) {
        setError("");
      }
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 4) {
      setError("Incorrect OTP!");
      return;
    }
    setLoading(true); // start loading
    try {
      const response = await fetch(
        "https://us-central1-supporty-staging.cloudfunctions.net/verifyOTP",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phNumber: phoneNumber,
            code: otpString,
            email: email,
            type: type,
          }),
        }
      );

      const result = await response.json();
      if (result.success) {
        try {
          const resultOTP = await dispatch(verifyOtp(otpString)).unwrap();
          if (registrationType) {
            if (registrationType === "invitation") {
              if (invitation.planType === "expert") {
                navigate(`/expert/dashboard?org=${invitation.orgId}`);
              } else {
                navigate(`/user/dashboard?org=${invitation.orgId}`);
              }
            } else if (registrationType === "owner") {
              if (organization.planType === "expert") {
                navigate(`/expert/dashboard?org=${organization.id}`);
              } else {
                navigate(`/user/dashboard?org=${organization.id}`);
              }
            }
          } else {
            if (user?.role === "Customer") {
              navigate("/user/dashboard");
            } else if (
              user?.role === "Expert" ||
              user?.role === "expert_owner"
            ) {
              navigate("/expert/dashboard");
            } else if (user?.role === "supporty_admin") {
              navigate("/admin/dashboard");
            } else {
              navigate("/signin");
            }
          }
        } catch (error) {
          console.log("Error verifying OTP:", error);
          setError("Failed to verify OTP. Please try again.");
        }
      } else {
        setError("Incorrect OTP!");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Incorrect OTP!");
    }
    setLoading(false); // stop loading
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setError("");
    setCanResend(false);
    setResendTimer(60); // Reset timer to 60 seconds

    try {
      const response = await fetch(
        "https://us-central1-supporty-staging.cloudfunctions.net/sendOTP",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phNumber: phoneNumber,
            email: email,
            type: type,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        setOtp(["", "", "", ""]);
        inputRefs.current[0]?.focus();
      } else {
        setError("Failed to resend OTP");
        setCanResend(true);
        setResendTimer(0);
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Failed to resend OTP");
      setCanResend(true);
      setResendTimer(0);
    }
  };

  return (
    <AuthLayout>
      <div className="flex items-center justify-center bg-white">
        <div className="flex-1 flex flex-col justify-center p-8 items-center h-screen">
          <div className="">
            <div className="text-center">
              <img
                src={OtpVerifyIcon}
                alt="Help Illustration"
                className="mx-auto"
              />
              <h2 className="text-[48px] nunito-extrabold text-[#0A0A0A] mt-6">
                Enter One Time Password
              </h2>
              <h2 className="text-[48px] nunito-extrabold text-[#0A0A0A] ">
                {" "}
                (OTP)
              </h2>
              <p className="mt-2 text-[24px] nunito-regular text-[#696969]">
                We have sent an OTP on your{" "}
                {type === "Email" ? "email" : "mobile"}.
              </p>
            </div>
            <div className="flex justify-center gap-4 mt-10">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  maxLength={1}
                  ref={(el) => (inputRefs.current[index] = el)}
                  required
                  disabled={loading ||fetchLoading}
                  className={`w-[100px] h-[64px]  text-[32px] text-center border-2 rounded-md
                    focus:outline-none focus:border-[#f4511e]
                    disabled:bg-gray-100 disabled:cursor-not-allowed 
                    ${
                      error
                        ? "border-[#D5332A] text-[#D5332A]"
                        : "border-[#E5E7EB]"
                    }
                    selection:bg-transparent`} // Added to improve selection behavior
                  onClick={(e) => {
                    // Select all text when clicking input
                    (e.target as HTMLInputElement).select();
                  }}
                />
              ))}
            </div>
            {error && (
              <div className="text-[#D5332A] text-center text-lg mt-4">
                {error}
              </div>
            )}
          </div>
          <div className="flex flex-col items-center gap-2 mt-10">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={ otp.join("").length !== 4 || loading }
              className="w-[320px] py-3 bg-[#F15D18] text-white rounded-lg text-lg font-medium
                hover:bg-[#F15D18]/90  disabled:cursor-not-allowed flex items-center justify-center"
            >
             {loading ? (
                      <>
                        <Loader className="h-5 w-5 animate-spin mr-2" />
                        Verifying...
                      </>
                    ) : (
                      <>Verify</>
                    )}
            </button>
            <div className="mt-6">
              <span className="flex justify-center nunito-regular text-[20px] gap-2 items-center">
                <span
                  style={{
                    color: "#696969",
                  }}
                >
                  Didn't receive code?
                </span>
                {resendTimer > 0 ? (
                  <span className="text-gray-500">
                    Resend OTP in {formatTime(resendTimer)}
                  </span>
                ) : (
                  <button
                    onClick={handleResendOTP}
                    disabled={fetchLoading || !canResend}
                    className="text-center text-base nunito-semibold text-[#F15D18] hover:text-primary-700 
                      disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    Resend OTP
                  </button>
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default OtpVerification;
